import React, { useEffect, useState } from 'react';
import { Link } from '../root/components/Link';
import { SignedIn, useSignUp } from '@clerk/clerk-react';
import { Spinner } from '../root/components/Spinner';
import { HoneyloveWindow } from '../models/HoneyloveWindow';
import { PhoneOtcValidation } from './PhoneOtcValidation';
import { MagicLinkValidation } from './MagicLinkValidation';
import { LoyaltySignupForm } from './LoyaltySignupForm';
import { CustomerDetails } from '../models/CustomerDetails.model';
import useSafariBfCacheInvalidator from '../hooks/useSafariBfCacheInvalidator';
import { assetPathFnState, environmentContextState } from '../recoil/atoms';
import { useRecoilValue } from 'recoil';
import { to } from 'await-to-js';
import useEmailSubscribe from '../hooks/actions/useEmailSubscribe';
import { CardBody } from '../components/CardBody';
import { CardFooter } from '../components/CardFooter';
import styles from './LoyaltyForm.module.scss';
import { LazyImageLoader } from '../product_detail/components/LazyImageLoader';
import usePhoneSubscribe from '../hooks/actions/usePhoneSubscribe';

enum SignUpSteps {
  EmailOrPhoneEntryStep,
  OneTimeCodeStep,
  WaitingForMagicLinkVerification,
}

declare const window: HoneyloveWindow;

const SignUpApp: React.FC = () => {
  const assetPathFn = useRecoilValue(assetPathFnState);
  const [step, setStep] = useState<SignUpSteps>(SignUpSteps.EmailOrPhoneEntryStep);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  useSafariBfCacheInvalidator(setIsLoading);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>();
  const [error, setError] = useState<string>('');

  const { signUp, isLoaded } = useSignUp();
  const emailSubscribe = useEmailSubscribe();
  const smsSubscribe = usePhoneSubscribe();

  const environmentContext = useRecoilValue(environmentContextState);
  const klaviyoLoyaltyFormKeys = environmentContext.klaviyo.loyaltySignup;

  const startOver = (err?: string) => {
    setStep(SignUpSteps.EmailOrPhoneEntryStep);
    setError(err || '');
  };

  const submitKlaviyoForm = async (customer: CustomerDetails) => {
    if (customer?.emailAddress) {
      await emailSubscribe(
        {
          klaviyoSiteId: environmentContext.klaviyoSiteId,
          email: customer?.emailAddress,
          klaviyoListId: klaviyoLoyaltyFormKeys.listId,
          klaviyoFormId: klaviyoLoyaltyFormKeys.formId,
          subscribeForNewsletter: true,
        },
        () => 0,
      );
    }
  };

  const handleSubmit = async (customer: CustomerDetails) => {
    setError('');
    setIsLoading(true);
    setCustomerDetails(customer);

    if (customer.phoneMarketingChecked && customer.phoneNumber) {
      await smsSubscribe({ phone: customer.phoneNumber });
    }

    if (customer.emailMarketingChecked && customer.emailAddress) {
      const [klaviyoErr] = await to(submitKlaviyoForm(customer));
      if (klaviyoErr && typeof window !== 'undefined') {
        window.Sentry?.captureException(klaviyoErr);
      }
    }

    setStep(customer.emailAddress ? SignUpSteps.WaitingForMagicLinkVerification : SignUpSteps.OneTimeCodeStep);
    setIsLoading(false);
  };

  return (
    <main role="main" className={styles.pageWrapper}>
      <CardBody>
        {step === SignUpSteps.EmailOrPhoneEntryStep && (
          <SignedIn>
            <LoggedInRedirect />
          </SignedIn>
        )}
        <div className={styles.hiveForm}>
          <div className={styles.formHeader}>
            <h1>Join the Hive</h1>
            <div className={styles.controls}>
              {step === SignUpSteps.EmailOrPhoneEntryStep && !isLoading && (
                <>
                  <div>
                    <Link dataTrackLabel="Already a member?" dataTrackLocation="SignInApp" href="/pages/sign-in" size="large">
                      Already a member?
                    </Link>
                  </div>
                  <div className={styles.active}>Sign up - it&apos;s free!</div>
                </>
              )}
            </div>
          </div>
          <div className={styles.formWrapper}>
            <div className={styles.formBody}>
              {isLoading && <Spinner visible={true} light={false} styleOverrides={{ spinner: { minHeight: 300 } }} />}
              {step === SignUpSteps.EmailOrPhoneEntryStep && !isLoading && <LoyaltySignupForm submit={handleSubmit} initialDetails={customerDetails} />}
              {isLoaded && step === SignUpSteps.WaitingForMagicLinkVerification && !isLoading && customerDetails?.emailAddress && (
                <MagicLinkValidation
                  emailAddress={customerDetails.emailAddress}
                  phoneNumber={customerDetails.phoneNumber || undefined}
                  clerkResource={{ type: 'signup', signUp, firstName: customerDetails.firstName, lastName: customerDetails.lastName }}
                  startOver={startOver}
                />
              )}
              {isLoaded && step === SignUpSteps.OneTimeCodeStep && !isLoading && customerDetails?.phoneNumber && (
                <PhoneOtcValidation
                  phone={customerDetails.phoneNumber}
                  clerkResource={{ type: 'signup', signUp, firstName: customerDetails.firstName, lastName: customerDetails.lastName }}
                  startOver={startOver}
                />
              )}
              {error && <div className={styles.error}>{error}</div>}
            </div>
            <div className={styles.hiveModels}>
              <LazyImageLoader src={assetPathFn('hive-models.jpg')} alt="Honeylove hive models" />
            </div>
          </div>
        </div>
      </CardBody>
      <CardFooter />
    </main>
  );
};

const LoggedInRedirect = () => {
  useEffect(() => {
    window.location.href = '/pages/profile';
  }, []);
  return <></>;
};

export default SignUpApp;
